import { Stack } from '@cardboard-ui/react';
import React from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { ExplicitOwners, isOwnerOfType } from './ExplicitOwners';
import { ImplicitOwners } from './ImplicitOwners';
import { LocationInfo } from './LocationInfo';
import {
  SpaceItemAccessDetails_node$data,
  SpaceItemAccessDetails_node$key,
} from './__generated__/SpaceItemAccessDetails_node.graphql';

const fragment = graphql`
  fragment SpaceItemAccessDetails_node on Node {
    id
    __typename
    ... on HasOwnerPermissionsInterface {
      canModifyOwners {
        value
      }
      ownerships {
        edges {
          node {
            ...Owner_ownership
            explicitOwner
            member {
              id
              status {
                value
              }
            }
            ownerOf {
              edges {
                node {
                  __typename
                }
              }
            }
          }
        }
      }
      ownershipPath {
        edges {
          node {
            __typename
            ... on Space {
              id
              name
              icon {
                ... on NamedIcon {
                  name
                }
              }
              color {
                light
                dark
              }
            }
            ... on Event {
              id
              name
            }
            ... on Folder {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const SpaceItemAccessDetails = ({
  node,
}: {
  node: SpaceItemAccessDetails_node$key;
}) => {
  const { id, ownerships, ownershipPath, canModifyOwners, __typename } =
    useFragment(fragment, node);

  if (!isOwnerOfType(__typename)) {
    throw new Error('Unable to load permissions');
  }

  const ownershipPathNodes = (ownershipPath?.edges?.filter((s) => !!s) || [])
    .map((edge) => edge?.node)
    .filter(notEmpty);

  const space = ownershipPathNodes.find(
    (node) => node?.__typename === 'Space',
  ) as { __typename: 'Space'; id: string; name: string };

  const spaceId = space.id || '';

  return (
    <Stack spacing={4}>
      <LocationInfo ownershipPathNodes={ownershipPathNodes} />
      <ExplicitOwners
        canModifyOwners={Boolean(canModifyOwners?.value)}
        ownerships={ownerships}
        nodeId={id}
        spaceId={spaceId}
        ownerOfType={__typename}
      />
      <ImplicitOwners ownerships={ownerships} nodeId={id} />
    </Stack>
  );
};

export type Node = NonNullable<SpaceItemAccessDetails_node$data>;

export interface OwnersPanelProps {
  nodeId: string;
  ownerships: Node['ownerships'];
}
